import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { PackagePlus, RotateCcw, Trash2 } from "lucide-react";

const PickLogModal = ({ isOpen, onClose, component, workOrder }) => {
  if (!isOpen || !component) return null;

  // Aggregate all warehouse entries (picks, returns, waste) from all lots
  const entries = [];
  
  if (component.lots) {
    component.lots.forEach((lot) => {
      // Add picks
      if (lot.picks) {
        lot.picks.forEach((pick) => {
          entries.push({
            timestamp: pick.picked_at,
            action: "pick",
            lotNumber: lot.lot_number,
            quantity: pick.picked_qty,
            user: pick.picked_by,
            unit: component.unit
          });
        });
      }
      
      // Add returns
      if (lot.returns) {
        lot.returns.forEach((ret) => {
          entries.push({
            timestamp: ret.returned_at,
            action: "return",
            lotNumber: lot.lot_number,
            quantity: ret.returned_qty,
            user: ret.returned_by,
            unit: component.unit,
            reason: ret.reason
          });
        });
      }
    });
  }
  
  // Add waste entries
  if (component.waste) {
    component.waste.forEach((wasteEntry) => {
      entries.push({
        timestamp: wasteEntry.logged_at,
        action: "waste",
        lotNumber: wasteEntry.lot_number,
        quantity: wasteEntry.waste_qty,
        user: wasteEntry.logged_by,
        unit: component.unit,
        reason: wasteEntry.reason
      });
    });
  }
  
  // Sort by timestamp (most recent first)
  entries.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Warehouse Log - {component.component_name}</DialogTitle>
          <p className="text-sm text-gray-500">All picks, returns, and waste entries</p>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto">
          {entries.length > 0 ? (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="border-b">
                  <th className="text-left p-2 font-semibold">Date/Time</th>
                  <th className="text-left p-2 font-semibold">Action</th>
                  <th className="text-left p-2 font-semibold">Lot Number</th>
                  <th className="text-right p-2 font-semibold">Quantity</th>
                  <th className="text-left p-2 font-semibold">User</th>
                  <th className="text-left p-2 font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-2">
                      {entry.timestamp ? format(new Date(entry.timestamp), 'MMM dd, HH:mm') : 'N/A'}
                    </td>
                    <td className="p-2">
                      <Badge 
                        variant="outline"
                        className={
                          entry.action === 'pick' ? 'bg-green-50 text-green-700 border-green-300' :
                          entry.action === 'return' ? 'bg-orange-50 text-orange-700 border-orange-300' :
                          'bg-red-50 text-red-700 border-red-300'
                        }
                      >
                        {entry.action === 'pick' && <PackagePlus className="w-3 h-3 mr-1" />}
                        {entry.action === 'return' && <RotateCcw className="w-3 h-3 mr-1" />}
                        {entry.action === 'waste' && <Trash2 className="w-3 h-3 mr-1" />}
                        {entry.action.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="p-2 font-mono text-xs">{entry.lotNumber}</td>
                    <td className="p-2 text-right font-semibold">
                      {entry.quantity.toFixed(2)} {entry.unit}
                    </td>
                    <td className="p-2 text-gray-600">{entry.user}</td>
                    <td className="p-2 text-gray-500 text-xs">{entry.reason || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No warehouse entries found for this component
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PickLogModal;
